<template>
  <component
    :is="ordered ? 'ol' : 'ul'"
    class="raw-body-3 text-[var(--raw-darkgrey)]"
  >
    <li v-for="(reference, index) in references" :key="index">
      <span v-if="reference.number !== undefined" data-marker>{{
        reference.number
      }}</span>
      <InlineMarkdown :message="reference.label" />
      <span v-if="reference.note" data-note>
        <InlineMarkdown :message="reference.note" />
      </span>
    </li>
  </component>
</template>

<script setup lang="ts">
import InlineMarkdown from '~/scopes/campaign/components/UI/InlineMarkdown.vue'
import type { Citation } from '~/scopes/campaign/data/sources'

// References as the client prints them: the wording, the official number when there is one,
// and the section's "Role: …" clause when it adds one.
//
// A sibling of `highlights/metal/SourcesList.vue`, not a mode of it: there the number *is*
// the bibliography position, here it is a label the caller may not have at all.
defineProps<{
  /** In the client's order — nothing is sorted here. */
  references: readonly Citation[]
  /** §2's per-story ordinals — `list-style`, not the `data-marker` of official numbers. */
  ordered?: boolean
}>()
</script>

<style scoped>
ul,
ol {
  display: grid;
  gap: 0.375rem;
  list-style: none;
  overflow-wrap: anywhere;

  li {
    line-height: 1.2;

    [data-marker] {
      font-size: 75%;
      vertical-align: top;
      margin-right: 0.25rem;
    }

    /* The role reads as its own annotation, so a stack stays scannable by role. */
    [data-note] {
      display: block;
      margin-top: 0.125rem;
      opacity: 0.8;
    }
  }
}

/* Only the ordered form renders an `ol`, so the element is the state. */
ol {
  padding-inline-start: 1.25rem;
  list-style: decimal;
}
</style>
